@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.SupportRoles

import com.zi.support.shared.CalendarSync
import com.zi.support.shared.EmptyState
import com.zi.support.shared.EmptyStates
import com.zi.support.shared.MeetingTiming
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Date
import org.w3c.dom.HTMLInputElement

internal object MeetingsScreen {
    private var stopHandle: dynamic = null
    private var clockHandle: Int? = null
    private var users: List<WebRecord> = emptyList()
    private var meetings: List<WebRecord> = emptyList()
    private var searchQuery = ""
    private var dateFilter = "All"
    private val dateFilters = listOf("All", "Today", "This week", "Upcoming", "Past")
    private const val DAY_MILLIS = 24 * 60 * 60 * 1000
    private var calendarEntries: List<GoogleCalendar.Entry> = emptyList()
    /** Set by [scheduleVisit]; the create form opens once support users load. */
    private var pendingKind: String? = null

    /** Header Refresh: reads meetings from the server (not the browser's saved copy) and says whether that worked. */
    fun refreshFromServer(done: () -> Unit) {
        val fs = FirebaseGateway.firestore
        promise(fs.getDocsFromServer(fs.query(FirebaseGateway.collection("meetings"), fs.orderBy("startAt", "desc"), fs.limit(100))), { snapshot ->
            val now = Date.now().toLong(); val sorted = records(snapshot).sortedBy { timestampMillis(it.data.startAt) }
            meetings = sorted.filter { timestampMillis(it.data.startAt) > now } + sorted.filter { timestampMillis(it.data.startAt) <= now }.reversed()
            renderMeetings(); syncCalendar(); toast("Meetings are up to date."); done()
        }, { message -> toast("Couldn't reach the server: $message", true); done() })
    }

    fun scheduleVisit() { pendingKind = CalendarSync.SITE_VISIT; WebApp.navigate("meetings") }

    fun stop() {
        cancelListener(stopHandle); stopHandle = null
        clockHandle?.let { window.clearInterval(it) }; clockHandle = null
    }

    fun render() {
        element("content")?.innerHTML = """
          <div class="page-intro"><div><span class="eyebrow">Schedule</span><h2>Meetings</h2><p>Plan work with one responsible owner and everyone who needs to attend.</p></div><button id="create-meeting" class="btn btn-primary"><span class="material-symbols-rounded">add</span>Create meeting</button></div>
          <div class="toolbar filter-panel"><div class="search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="meeting-search">Search meetings</label><input id="meeting-search" class="input search" placeholder="Search meetings, reason or assignee…"></div></div>
          <p class="page-note" id="calendar-note" hidden><span class="material-symbols-rounded">calendar_month</span><span>Add your meetings to your Google Calendar and see your other events here.</span><button id="calendar-connect" class="btn">Connect Google Calendar</button></p>
          <div class="date-filters" id="meeting-date-filters">${dateFilters.joinToString("") { "<button class=\"btn date-filter-chip\" data-date-filter=\"${escape(it)}\">${escape(it)}</button>" }}</div>
          <div id="meeting-list" class="mt-groups"><div class="empty">Loading meetings…</div></div>
        """.trimIndent()
        click("create-meeting") { openCreate() }
        click("calendar-connect") { GoogleCalendar.connect(WebApp.currentContactEmail) { syncCalendar() } }
        GoogleCalendar.preload(); paintCalendarNote()
        type("meeting-search") { searchQuery = input("meeting-search").trim(); renderMeetings() }
        document.querySelectorAll("[data-date-filter]").let { nodes ->
            for (i in 0 until nodes.length) nodes.item(i)?.addEventListener("click", { event ->
                dateFilter = (event.currentTarget as org.w3c.dom.HTMLElement).getAttribute("data-date-filter").orEmpty()
                paintDateFilters(); renderMeetings()
            })
        }
        paintDateFilters()
        loadUsers()
        stop()
        val fs = FirebaseGateway.firestore
        val query = fs.query(FirebaseGateway.collection("meetings"), fs.orderBy("startAt", "desc"), fs.limit(100))  // newest 100 by start (upcoming ones first)
        stopHandle = FirebaseGateway.listenQuery(query, onRows = { rows ->
            val now=Date.now().toLong();val sorted=rows.sortedBy { timestampMillis(it.data.startAt) }
            meetings = sorted.filter { timestampMillis(it.data.startAt)>now } + sorted.filter { timestampMillis(it.data.startAt)<=now }.reversed()
            renderMeetings(); syncCalendar()
        }, onError = { element("meeting-list")?.innerHTML = emptyState(EmptyState("Couldn't load meetings", it), "event_busy") })
        clockHandle = window.setInterval({ renderMeetings() }, 30_000)
    }

    private fun paintDateFilters() {
        document.querySelectorAll("[data-date-filter]").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as org.w3c.dom.HTMLElement
                node.classList.toggle("active", node.getAttribute("data-date-filter") == dateFilter)
            }
        }
    }

    private fun paintCalendarNote() {
        val note = element("calendar-note") ?: return
        if (GoogleCalendar.configured && !GoogleCalendar.connected()) note.removeAttribute("hidden") else note.setAttribute("hidden", "")
    }

    /** Copies this person's upcoming meetings into their Google Calendar and reloads their other events. */
    private fun syncCalendar() {
        paintCalendarNote()
        val uid = FirebaseGateway.currentUser()?.uid as? String ?: return
        val mine = meetings.filter { CalendarSync.involves(uid, field(it.data,"assignedToUid"), field(it.data,"createdByUid"), dynamicStrings(it.data.attendeeUids)) }
            .map { GoogleCalendar.Meeting(it.id, meetingTitle(it), CalendarSync.eventDescription(it.id, field(it.data,"reason")), field(it.data,"customerName"), timestampMillis(it.data.startAt)) }
        GoogleCalendar.sync(uid, mine) { calendarEntries = it; paintCalendarNote(); renderMeetings() }
    }

    private fun meetingTitle(record: WebRecord) = CalendarSync.eventTitle(field(record.data,"kind"), field(record.data,"title"), field(record.data,"customerName"))

    private fun matchesDate(start: Long): Boolean {
        val now = Date.now().toLong()
        val todayStart = js("new Date(new Date().setHours(0,0,0,0)).getTime()") as Double
        return when (dateFilter) {
            "Today" -> start >= todayStart && start < todayStart + DAY_MILLIS
            "This week" -> start >= todayStart && start < todayStart + 7 * DAY_MILLIS
            "Upcoming" -> start > now
            "Past" -> start <= now
            else -> true
        }
    }

    private fun visibleMeetings(): List<WebRecord> = meetings.filter { record ->
        matchesDate(timestampMillis(record.data.startAt).toLong()) && (searchQuery.isBlank() || listOf(
            meetingTitle(record), field(record.data, "reason"), field(record.data, "assignedToName")
        ).joinToString(" ").contains(searchQuery, ignoreCase = true))
    }

    private fun renderMeetings() {
        val visible = visibleMeetings()
        val entries = calendarEntries.filter { matchesDate(it.begin.toLong()) && (searchQuery.isBlank() || "${it.title} ${it.location}".contains(searchQuery, ignoreCase = true)) }
        element("meeting-list")?.innerHTML = if (visible.isEmpty() && entries.isEmpty()) {
            if (meetings.isEmpty() && calendarEntries.isEmpty()) emptyState(EmptyStates.meetings, "event_available") else emptyState(EmptyStates.noMatches("meetings"), "search")
        } else sectioned(visible) +
            (if (entries.isEmpty()) "" else "<h3 class=\"mt-section\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">calendar_month</span>From your Google Calendar<small>${entries.size}</small></h3><div class=\"mt-list\">" + entries.joinToString("") { calendarCard(it) } + "</div>")
        bindMeetingCards(visible)
    }

    /** Meetings under Today (earliest first), Upcoming (earliest first) and Past (newest first). */
    private fun sectioned(records: List<WebRecord>): String {
        val now = Date.now().toLong()
        val midnight = Date().also { it.asDynamic().setHours(0, 0, 0, 0) }.getTime().toLong()
        val groups = records.groupBy { MeetingTiming.section(timestampMillis(it.data.startAt).toLong(), now, midnight) }
        return MeetingTiming.sections.mapNotNull { name ->
            val rows = groups[name] ?: return@mapNotNull null
            val sorted = if (name == MeetingTiming.PAST) rows.sortedByDescending { timestampMillis(it.data.startAt) } else rows.sortedBy { timestampMillis(it.data.startAt) }
            "<h3 class=\"mt-section\">${escape(name)}<small>${rows.size}</small></h3><div class=\"mt-list\">" + sorted.joinToString("") { card(it) } + "</div>"
        }.joinToString("")
    }

    /** "28" over "OCT", tinted by the card's state. */
    private fun dateTile(millis: Double, large: Boolean = false): String {
        val date = Date(millis)
        val month = date.toLocaleDateString(emptyArray(), dateLocaleOptions { this.month = "short" }).uppercase()
        return "<span class=\"mt-date${if (large) " mt-date-lg" else ""}\" aria-hidden=\"true\"><b>${date.getDate()}</b><small>${escape(month)}</small></span>"
    }

    private fun clock(millis: Double) = Date(millis).toLocaleTimeString(emptyArray(), dateLocaleOptions { hour = "2-digit"; minute = "2-digit" })

    /** Up to three attendee avatars overlapping, then "+N". */
    private fun avatarStack(names: List<String>): String {
        if (names.isEmpty()) return "<span class=\"mt-people mt-none\">No attendees</span>"
        return "<span class=\"mt-people\" title=\"${escape(names.joinToString(", "))}\">" + names.take(3).joinToString("") { avatarHtml(it) } +
            (if (names.size > 3) "<span class=\"mt-more\">+${names.size - 3}</span>" else "") + "<span class=\"visually-hidden\">${names.size} attendees</span></span>"
    }

    /** One of the person's own Google Calendar events; opens it in Google Calendar. */
    private fun calendarCard(entry: GoogleCalendar.Entry): String {
        val time = if (entry.allDay) "All day" else clock(entry.begin)
        val location = if (entry.location.isBlank()) "" else "<p class=\"mt-reason\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">location_on</span>${escape(entry.location)}</p>"
        return """
          <a class="card mt-card mt-calendar" href="${escape(entry.link)}" target="_blank" rel="noopener">
            ${dateTile(entry.begin)}
            <div class="mt-body"><div class="mt-top"><span class="mt-kind mt-kind-calendar"><span class="material-symbols-rounded" aria-hidden="true">event</span>Calendar</span><span class="mt-when">${escape(time)}</span></div>
              <h3 class="mt-title">${escape(entry.title)}</h3>$location</div>
          </a>
        """.trimIndent()
    }

    private fun loadUsers() {
        promise(FirebaseGateway.getCollection("supportUsers"), { snapshot ->
            val docs = snapshot.docs
            users = (0 until (docs.length as Int)).map { index ->
                val doc = docs[index]
                WebRecord(doc.id as String, doc.ref.path as String, doc.data())
            }.filter { field(it.data, "uid").isNotBlank() }.sortedBy { field(it.data, "username").lowercase() }
            pendingKind?.let { pendingKind = null; openCreate(it) }
        })
    }

    private fun card(record: WebRecord): String {
        val d = record.data
        val attendees = dynamicStrings(d.attendeeNames) + dynamicStrings(d.externalAttendeeEmails)
        val start = timestampMillis(d.startAt).toLong(); val now = Date.now().toLong(); val state = MeetingTiming.state(start, now); val relative = MeetingTiming.relativeLabel(start, now)
        val visit = field(d, "kind") == CalendarSync.SITE_VISIT
        val owner = field(d, "assignedToName").ifBlank { "Not assigned" }
        val reason = field(d, "reason")
        return """
          <button type="button" class="card mt-card mt-$state" data-meeting-path="${escape(record.path)}" aria-label="Open ${escape(meetingTitle(record))}">
            ${dateTile(start.toDouble())}
            <div class="mt-body">
              <div class="mt-top"><span class="mt-kind${if (visit) " mt-kind-visit" else ""}"><span class="material-symbols-rounded" aria-hidden="true">${if (visit) "location_on" else "groups"}</span>${MeetingTiming.kindLabel(field(d, "kind"))}</span><span class="mt-when">${escape(clock(start.toDouble()))} · ${escape(relative)}</span></div>
              <h3 class="mt-title">${escape(meetingTitle(record))}</h3>
              ${if (reason.isBlank()) "" else "<p class=\"mt-reason\">${escape(reason)}</p>"}
              <div class="mt-foot"><span class="mt-owner">${avatarHtml(owner)}<span>${escape(owner)}</span></span>${avatarStack(attendees)}</div>
            </div>
          </button>
        """.trimIndent()
    }

    private fun openCreate(kind: String = "meeting") {
        if (users.isEmpty()) { toast("Support users are still loading. Try again in a moment.", true); loadUsers(); return }
        val options = users.joinToString("") { "<option value=\"${escape(field(it.data,"uid"))}\">${escape(field(it.data,"username"))}</option>" }
        val attendeeRows = users.joinToString("") {
            val uid = escape(field(it.data,"uid")); val name = escape(field(it.data,"username")); val email = escape(field(it.data,"email"))
            "<label class=\"meeting-attendee-option\"><input type=\"checkbox\" name=\"meeting-attendee\" value=\"$uid\"><span class=\"meeting-check\"><span class=\"material-symbols-rounded\">check</span></span><span><strong>$name</strong>${if(email.isBlank())""else"<small>$email</small>"}</span></label>"
        }
        val visit = kind == CalendarSync.SITE_VISIT
        modal("Schedule a meeting or visit", """
          <div class="form-grid">
            <div class="field"><label>Type</label><select id="meeting-kind"><option value="meeting">Meeting</option><option value="${CalendarSync.SITE_VISIT}"${if (visit) " selected" else ""}>Site visit</option></select></div>
            <div class="field" id="meeting-customer-field"${if (visit) "" else " hidden"}><label>Customer</label><input id="meeting-customer" class="input" maxlength="200"></div>
            <div class="field span-2"><label>Title</label><input id="meeting-title" class="input" maxlength="300" placeholder="What is the meeting about?"></div>
            <div class="field span-2"><label>Reason</label><textarea id="meeting-reason" maxlength="3000" placeholder="Purpose, context or topics to cover"></textarea></div>
            <div class="field"><label>Date and time</label><input id="meeting-start" class="input" type="datetime-local"></div>
            <div class="field"><label>Assigned person</label><select id="meeting-owner">$options</select></div>
            <div class="field span-2"><div class="field-label-row"><label>App attendees</label><span id="meeting-attendee-count" class="selection-count">0 selected</span></div><div id="meeting-attendees" class="meeting-attendee-picker">$attendeeRows</div><span class="field-help">Tick every app user who can attend.</span></div>
            <div class="field span-2"><label>External attendee emails (optional)</label><textarea id="meeting-external" placeholder="name@company.com, another@company.com"></textarea><span class="field-help">Separate multiple email addresses with commas or new lines.</span></div>
          </div>
        """.trimIndent(), "<button id=\"save-meeting\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\">event_available</span>Create meeting</button>")
        change("meeting-start") { (document.getElementById("meeting-start") as? HTMLInputElement)?.asDynamic()?.blur() }
        document.querySelectorAll("input[name='meeting-attendee']").let { nodes -> for(i in 0 until nodes.length) nodes.item(i)?.addEventListener("change", { updateAttendeeCount() }) }
        change("meeting-kind") {
            val customerField = element("meeting-customer-field")
            if (select("meeting-kind") == CalendarSync.SITE_VISIT) customerField?.removeAttribute("hidden") else customerField?.setAttribute("hidden", "")
        }
        click("save-meeting") { saveMeeting() }
    }

    private fun saveMeeting() {
        val title=input("meeting-title").trim(); val reason=textarea("meeting-reason").trim(); val start=input("meeting-start")
        val ownerUid=select("meeting-owner"); val owner=users.firstOrNull { field(it.data,"uid")==ownerUid }
        val millis=Date.parse(start)
        if(title.isBlank()||reason.isBlank()||start.isBlank()||owner==null||millis.isNaN()){toast("Add the title, reason, date, time and assigned person.",true);return}
        val kind=select("meeting-kind"); val customer=if(kind==CalendarSync.SITE_VISIT) input("meeting-customer").trim() else ""
        if(kind==CalendarSync.SITE_VISIT&&customer.isBlank()){toast("Add the customer for this site visit.",true);return}
        val attendeeUids=mutableListOf<String>()
        val attendeeNodes=document.querySelectorAll("input[name='meeting-attendee']")
        for(i in 0 until attendeeNodes.length) (attendeeNodes.item(i) as? HTMLInputElement)?.let { if(it.checked) attendeeUids += it.value }
        val attendeeUsers=users.filter { field(it.data,"uid") in attendeeUids }
        val external=textarea("meeting-external").split(Regex("[,;\\n]+")).map{it.trim().lowercase()}.filter{it.isNotBlank()}.distinct()
        val invalid=external.firstOrNull { !it.matches(Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")) }
        if(invalid!=null){toast("Check the external email: $invalid",true);return}
        val user=FirebaseGateway.currentUser() ?: return
        val button=element("save-meeting");button?.setAttribute("disabled","");button?.textContent="Creating…"
        promise(FirebaseGateway.add("meetings",json(
            "kind" to kind,"customerName" to customer,"title" to title,"reason" to reason,"startAt" to FirebaseGateway.timestampFromMillis(millis),"assignedToUid" to ownerUid,
            "assignedToName" to field(owner.data,"username"),"assignedToEmail" to field(owner.data,"email"),
            "attendeeUids" to attendeeUids.toTypedArray(),"attendeeNames" to attendeeUsers.map{field(it.data,"username")}.toTypedArray(),
            "internalAttendeeEmails" to attendeeUsers.map{field(it.data,"email")}.filter{it.isNotBlank()}.toTypedArray(),
            "externalAttendeeEmails" to external.toTypedArray(),"attendeeEmails" to (attendeeUsers.map{field(it.data,"email")}.filter{it.isNotBlank()}+external).distinct().toTypedArray(),
            "createdByUid" to user.uid,"createdByName" to WebApp.currentName,"status" to "scheduled",
            "createdAt" to FirebaseGateway.serverTimestamp(),"updatedAt" to FirebaseGateway.serverTimestamp()
        )),{toast(if(kind==CalendarSync.SITE_VISIT)"Site visit scheduled." else "Meeting created.");closeModal()},{message->button?.removeAttribute("disabled");button?.innerHTML="<span class=\"material-symbols-rounded\">event_available</span>Create meeting";toast(message,true)})
    }

    private fun dynamicStrings(value: dynamic): List<String> {
        if (value == null) return emptyList()
        val length = value.length as? Int ?: return emptyList()
        return (0 until length).mapNotNull { value[it] as? String }.filter { it.isNotBlank() }
    }

    private fun updateAttendeeCount() {
        val nodes=document.querySelectorAll("input[name='meeting-attendee']"); var count=0
        for(i in 0 until nodes.length) if((nodes.item(i) as? HTMLInputElement)?.checked==true) count++
        element("meeting-attendee-count")?.textContent="$count selected"
    }

    private fun bindMeetingCards(records: List<WebRecord>) {
        document.querySelectorAll("[data-meeting-path]").let { nodes -> for(i in 0 until nodes.length) {
            val node=nodes.item(i) as? org.w3c.dom.HTMLElement ?: continue
            val open={ records.firstOrNull { it.path==node.getAttribute("data-meeting-path") }?.let(::openDetails) }
            node.addEventListener("click", { open() })
        } }
    }

    private fun openDetails(record: WebRecord) {
        val d=record.data; val internal=dynamicStrings(d.attendeeNames); val external=dynamicStrings(d.externalAttendeeEmails)
        val start=timestampMillis(d.startAt).toLong();val now=Date.now().toLong();val state=MeetingTiming.state(start,now);val relative=MeetingTiming.relativeLabel(start,now)
        val visit=field(d,"kind")==CalendarSync.SITE_VISIT
        val full=Date(start.toDouble()).toLocaleDateString(emptyArray(), dateLocaleOptions { weekday = "long"; day = "numeric"; month = "long"; year = "numeric" })
        fun person(name:String,note:String)="<li class=\"mtd-person\">${avatarHtml(name)}<div><strong>${escape(name)}</strong><small>${escape(note)}</small></div></li>"
        val people=internal.map{person(it,"Support team")}+external.map{"<li class=\"mtd-person\"><span class=\"mtd-mail\" aria-hidden=\"true\"><span class=\"material-symbols-rounded\">mail</span></span><div><a href=\"mailto:${escape(it)}\">${escape(it)}</a><small>External</small></div></li>"}
        fun fact(icon:String,label:String,value:String)="<div class=\"mtd-fact\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span><div><small>${escape(label)}</small><div>$value</div></div></div>"
        val owner=field(d,"assignedToName").ifBlank{"Not recorded"}
        modal(if(visit)"Site visit details" else "Meeting details", """
          <div class="mtd mt-$state">
            <div class="mtd-hero">${dateTile(start.toDouble(), large = true)}
              <div class="mtd-head"><div class="mtd-chips"><span class="mt-kind${if (visit) " mt-kind-visit" else ""}"><span class="material-symbols-rounded" aria-hidden="true">${if (visit) "location_on" else "groups"}</span>${MeetingTiming.kindLabel(field(d,"kind"))}</span><span class="mtd-state">${escape(if (state == "upcoming") relative.replaceFirstChar { it.uppercase() } else "Ended")}</span></div>
                <h2>${escape(meetingTitle(record))}</h2><p><span class="material-symbols-rounded" aria-hidden="true">schedule</span>${escape(full)} · ${escape(clock(start.toDouble()))}</p></div></div>
            <div class="mtd-facts">
              ${fact("person","Assigned to","<span class=\"mtd-inline\">${avatarHtml(owner)}${escape(owner)}</span>")}
              ${if (visit) fact("storefront","Customer",escape(field(d,"customerName").ifBlank{"Not recorded"})) else ""}
              ${fact("notes","Reason","<span class=\"mtd-reason\">${escape(field(d,"reason").ifBlank{"Not recorded"})}</span>")}
            </div>
            <section class="mtd-people"><h3>Attendees <span>${internal.size + external.size}</span></h3>
              ${if (people.isEmpty()) "<p class=\"empty-inline\">No attendees were added.</p>" else "<ul>${people.joinToString("")}</ul>"}</section>
          </div>
        """.trimIndent(), if (SupportRoles.isSuperAdmin(WebApp.currentName)) "<button id=\"delete-meeting\" class=\"btn btn-danger\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">delete</span>Delete ${if (visit) "site visit" else "meeting"}</button>" else "", wide = false)
        // Super admins only (SupportRoles; firestore.rules checks the role too). The first tap arms it.
        click("delete-meeting") {
            val button = element("delete-meeting") ?: return@click
            if (button.getAttribute("data-armed") != "1") { button.setAttribute("data-armed", "1"); button.textContent = "Tap again to delete for everyone"; return@click }
            button.asDynamic().disabled = true; button.textContent = "Deleting…"
            promise(FirebaseGateway.delete(record.path), { closeAllModals(); toast("${if (visit) "Site visit" else "Meeting"} deleted.") },
                { message -> button.asDynamic().disabled = false; button.removeAttribute("data-armed"); button.textContent = "Delete"; toast(message, true) })
        }
    }
}
